import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiUrl } from './config';

function Sale() {
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [invoice, setInvoice] = useState({ 
        customer_id: '', 
        date: new Date().toISOString().split('T')[0], 
        total_amount: 0,
        paid_amount: 0,
        payment_mode: 'Cash'
    });
    const [prevBalance, setPrevBalance] = useState(0);

    const [items, setItems] = useState([]);
    const [currentItem, setCurrentItem] = useState({ product_id: '', quantity: 0, price: 0, discount_percent: 0, discount_amount: 0, total: 0 });

    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const editId = queryParams.get('edit');

    useEffect(() => {
        fetchData();
        if (editId) {
            fetchInvoiceForEdit(editId);
        }
    }, [editId]);

    useEffect(() => {
        if (invoice.customer_id && !editId) {
            fetchCustomerBalance(invoice.customer_id);
        } else if (!invoice.customer_id) {
            setPrevBalance(0);
        }
    }, [invoice.customer_id, editId]);

    const fetchCustomerBalance = async (id) => {
        try {
            const res = await axios.get(`${getApiUrl()}/customers/${id}/balance`);
            setPrevBalance(res.data.balance || 0);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchData = async () => {
        try {
            const [prodRes, custRes] = await Promise.all([
                axios.get(`${getApiUrl()}/products`),
                axios.get(`${getApiUrl()}/customers`)
            ]);
            setProducts(prodRes.data);
            setCustomers(custRes.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchInvoiceForEdit = async (id) => {
        try {
            const res = await axios.get(`${getApiUrl()}/sales/${id}`);
            const data = res.data;
            setInvoice({
                customer_id: data.customer_id,
                date: new Date(data.date).toISOString().split('T')[0],
                total_amount: data.total_amount,
                paid_amount: data.paid_amount || 0,
                payment_mode: data.payment_mode || 'Cash'
            });
            setItems(data.items.map(item => ({
                product_id: item.product_id,
                product_name: item.product_name,
                quantity: item.quantity,
                price: item.price,
                discount_percent: parseFloat(item.discount_percent) || 0,
                discount_amount: parseFloat(item.discount_amount) || 0,
                total: item.total
            })));
        } catch (err) {
            console.error(err);
        }
    };

    const addItem = () => {
        if (!currentItem.product_id || currentItem.quantity <= 0) return;
        const product = products.find(p => p.id === parseInt(currentItem.product_id));
        
        // Stock check needs to consider if we are editing an existing item in the invoice
        // For simplicity, we just check current stock. In a real app, we'd add back the original quantity.
        if (currentItem.quantity > product.stock) {
            alert(`Insufficient stock! Current stock: ${product.stock}`);
            return;
        }

        const newItem = { ...currentItem, product_name: product.name };
        setItems([...items, newItem]);
        setCurrentItem({ product_id: '', quantity: 0, price: 0, discount_percent: 0, discount_amount: 0, total: 0 });
    };

    const removeItem = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        setItems(newItems);
    };

    useEffect(() => {
        const netTotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
        setInvoice(prev => (prev.total_amount === netTotal ? prev : { ...prev, total_amount: netTotal }));
    }, [items]);

    const handleSale = async (e) => {
        e.preventDefault();
        if (items.length === 0) {
            alert("Add at least one item");
            return;
        }
        
        const payload = {
            ...invoice,
            paid_amount: parseFloat(invoice.paid_amount) || 0,
            items
        };
        
        console.log("Submitting Sale:", payload);
        try {
            if (editId) {
                await axios.put(`${getApiUrl()}/sales/${editId}`, payload);
                alert('Sale Invoice Updated');
                navigate('/reports/sale');
            } else {
                await axios.post(`${getApiUrl()}/sales`, payload);
                alert('Sale Invoice Recorded');
                setItems([]);
                setInvoice({ 
                    customer_id: '', 
                    date: new Date().toISOString().split('T')[0], 
                    total_amount: 0,
                    paid_amount: 0,
                    payment_mode: 'Cash'
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        const qty = parseFloat(currentItem.quantity) || 0;
        const price = parseFloat(currentItem.price) || 0;
        const gross = qty * price;
        const discountPercent = Math.min(100, Math.max(0, parseFloat(currentItem.discount_percent) || 0));
        const discountAmount = gross * (discountPercent / 100);
        const total = Math.max(0, gross - discountAmount);
        setCurrentItem(prev => ({
            ...prev,
            discount_percent: discountPercent,
            discount_amount: discountAmount,
            total
        }));
    }, [currentItem.quantity, currentItem.price, currentItem.discount_percent]);

    return (
        <Layout>
            <div className="container mt-4">
                <h2>{editId ? `Edit Sale Invoice #${editId}` : 'New Sale Invoice'}</h2>
                <div className="card p-4 mb-4 shadow-sm border-0">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Customer</label>
                            <select className="form-select" value={invoice.customer_id} onChange={e => setInvoice({...invoice, customer_id: e.target.value})} required>
                                <option value="">Select Customer</option>
                                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Date</label>
                            <input type="date" className="form-control" value={invoice.date} onChange={e => setInvoice({...invoice, date: e.target.value})} onFocus={(e) => e.target.select()} required />
                        </div>
                    </div>
                </div>

                <div className="card p-4 mb-4 bg-light">
                    <h5>Add Items</h5>
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="form-label">Product</label>
                            <select className="form-select" value={currentItem.product_id} onChange={e => {
                                const prod = products.find(p => p.id === parseInt(e.target.value));
                                setCurrentItem({...currentItem, product_id: e.target.value, price: prod ? prod.price : 0});
                            }}>
                                <option value="">Select Product</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name} (Stock: {p.stock})</option>)}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Quantity</label>
                            <input type="number" className="form-control" value={currentItem.quantity} onChange={e => setCurrentItem({...currentItem, quantity: parseInt(e.target.value)})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Price</label>
                            <input type="number" className="form-control" value={currentItem.price} onChange={e => setCurrentItem({...currentItem, price: parseFloat(e.target.value)})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Disc %</label>
                            <input
                                type="number"
                                className="form-control"
                                value={currentItem.discount_percent}
                                onChange={e => setCurrentItem({ ...currentItem, discount_percent: parseFloat(e.target.value) })}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Total</label>
                            <input type="number" className="form-control" value={currentItem.total} readOnly />
                        </div>
                        <div className="col-12 d-grid d-md-flex justify-content-end">
                            <button type="button" className="btn btn-primary" onClick={addItem}>Add Item</button>
                        </div>
                    </div>
                </div>

                <div className="table-responsive mb-4">
                    <table className="table table-bordered">
                        <thead className="table-dark">
                            <tr>
                                <th>Product</th>
                                <th>Qty</th>
                                <th>Price</th>
                                <th>Disc %</th>
                                <th>Total</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.product_name}</td>
                                    <td>{item.quantity}</td>
                                    <td>{item.price}</td>
                                    <td>{parseFloat(item.discount_percent || 0).toFixed(2)}</td>
                                    <td>{item.total}</td>
                                    <td><button className="btn btn-danger btn-sm" onClick={() => removeItem(index)}>Remove</button></td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <th colSpan="3" className="text-end">Gross Total</th>
                                <th colSpan="3">₹ {items.reduce((sum, item) => sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0)), 0).toFixed(2)}</th>
                            </tr>
                            <tr>
                                <th colSpan="3" className="text-end text-warning align-middle">Item Discount Total</th>
                                <th colSpan="3">₹ {items.reduce((sum, item) => sum + (parseFloat(item.discount_amount) || 0), 0).toFixed(2)}</th>
                            </tr>
                            <tr>
                                <th colSpan="3" className="text-end">Previous Balance</th>
                                <th colSpan="3">₹ {prevBalance.toFixed(2)}</th>
                            </tr>
                            <tr>
                                <th colSpan="3" className="text-end">Net Invoice Total</th>
                                <th colSpan="3">₹ {invoice.total_amount.toFixed(2)}</th>
                            </tr>
                            <tr className="table-secondary">
                                <th colSpan="3" className="text-end fw-bold">Grand Total (Total Balance)</th>
                                <th colSpan="3" className="fw-bold">₹ {(prevBalance + invoice.total_amount).toFixed(2)}</th>
                            </tr>
                            <tr>
                                <th colSpan="3" className="text-end text-success align-middle">Amount Paid</th>
                                <th colSpan="3">
                                    <div className="input-group input-group-sm">
                                        <input 
                                            type="number" 
                                            className="form-control" 
                                            style={{ maxWidth: '120px' }}
                                            value={invoice.paid_amount} 
                                            onChange={e => {
                                                const val = parseFloat(e.target.value);
                                                setInvoice(prev => ({...prev, paid_amount: isNaN(val) ? 0 : val}));
                                            }}
                                            onFocus={(e) => e.target.select()}
                                        />
                                        <select 
                                            className="form-select" 
                                            value={invoice.payment_mode} 
                                            onChange={e => setInvoice({...invoice, payment_mode: e.target.value})}
                                        >
                                            <option value="Cash">Cash</option>
                                            <option value="Bank Transfer">Bank Transfer</option>
                                            <option value="UPI">UPI</option>
                                            <option value="Credit">Credit</option>
                                        </select>
                                    </div>
                                </th>
                            </tr>
                            <tr>
                                <th colSpan="3" className="text-end text-danger fw-bold">Remaining Balance</th>
                                <th colSpan="3" className="fw-bold">₹ {(prevBalance + invoice.total_amount - invoice.paid_amount).toFixed(2)}</th>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <div className="text-end mb-5">
                    <button className="btn btn-primary btn-lg w-100 w-md-auto" onClick={handleSale}>{editId ? 'Update Invoice' : 'Save Invoice'}</button>
                    {editId && <button className="btn btn-secondary btn-lg w-100 w-md-auto ms-md-2 mt-2 mt-md-0" onClick={() => navigate('/reports/sale')}>Cancel</button>}
                </div>
            </div>
        </Layout>
    );
}

export default Sale;
